/**
 * Referral attribution — captures ?ref=<value> from the URL on any
 * page load and stores it in sessionStorage so downstream subscribe
 * flows can attribute the signup to a specific referral channel.
 *
 * Current channels wired:
 *   - `fw` = forwarded WhatsApp digest (the "forward to a friend"
 *            nudge appended to every daily digest send). Persisted
 *            to the subscriber record as `subscribe_source =
 *            "digest_forward"` (see backend `ALLOWED_SOURCES`).
 *
 * Kept intentionally minimal — this is a measurement primitive, not
 * a full referral mechanic. Extend by mapping additional `?ref=`
 * values to `subscribe_source` strings in `REF_TO_SOURCE` below.
 *
 * sessionStorage (not localStorage) is deliberate: attribution is
 * per-visit, so a returning visitor who opens the site normally
 * shouldn't be tagged with a stale referral from three weeks ago.
 */

const KEY = "islandsdaily.subscribe_source";

// Map ?ref=<value> → subscribe_source value stored on the subscriber
// record. Keep in sync with backend `ALLOWED_SOURCES` in server.py.
const REF_TO_SOURCE = {
  fw: "digest_forward",
};

/**
 * Read `?ref=...` from the current URL and, if it maps to a known
 * source, persist that source to sessionStorage so the subscribe
 * modal picks it up on submit. Safe to call multiple times — later
 * calls with the same `?ref` are no-ops; calls with a fresh `?ref`
 * override the previous value (a visitor who taps a new referral
 * link mid-session gets attributed to the latest tap).
 *
 * Returns the mapped source string, or "" if the URL has no `?ref`
 * or the value isn't in REF_TO_SOURCE.
 */
export function captureReferralFromUrl() {
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim().toLowerCase();
    if (!ref) return "";
    const src = REF_TO_SOURCE[ref];
    if (!src) return "";
    window.sessionStorage.setItem(KEY, src);
    return src;
  } catch {
    return "";
  }
}

/**
 * Retrieve the currently-attributed subscribe_source, or "" if none.
 * Called by the subscribe modal(s) at submit time and attached to
 * the /api/subscribe payload as `subscribe_source`.
 */
export function getSubscribeSource() {
  if (typeof window === "undefined") return "";
  try {
    return window.sessionStorage.getItem(KEY) || "";
  } catch {
    return "";
  }
}
